// useRewardsSummary — rank, points and claimable GRAM for one wallet, from
// GET /v1/wallets/:address/rewards-summary.
//
// The endpoint reads materialized reward/referral/quest/creator tables only —
// it starts no projector, touches no chain and runs no claim path — so it is
// safe to call on every page view.
//
// Before this, the Rewards page printed literal zeros. Those were HONEST (the
// page never invented a tier or a balance), but they were also unconditional:
// a user with real points would have seen the same zeros. Binding turns an
// honest placeholder into an honest reading.

import { useEffect, useRef, useState } from 'react'
import { apiFetch } from '../api/client'
import { canonicalTonAddress } from '../lib/tonAddress'

export type RewardsSummary = {
  rank: string
  points_total: string
  platform_volume_gram: string
  trade_rewards_claimable_gram: string
  referral_rewards_claimable_gram: string
  creator_rewards_claimable_gram: string
  claimed_rewards_gram: string
  total_rewards_claimable_gram: string
  referrals?: {
    count: number
    volume_gram: string
    reward_gram_total: string
    reward_usd_total: string
  }
  quests?: {
    points_total: string
    completed_count: number
    daily_completed: number
    permanent_iterations: number
  }
  creator_rewards?: {
    token_count: number
    claimable_gram: string
    claimed_gram: string
  }
}

export type RewardsStatus = 'loading' | 'ready' | 'offline'

export type UseRewardsSummary = {
  status: RewardsStatus
  summary: RewardsSummary | null
}

export function useRewardsSummary(address: string | null): UseRewardsSummary {
  const canonical = canonicalTonAddress(address)
  const [status, setStatus] = useState<RewardsStatus>('loading')
  const [summary, setSummary] = useState<RewardsSummary | null>(null)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  useEffect(() => {
    if (!canonical) {
      setStatus('ready')
      setSummary(null)
      return
    }
    setStatus('loading')
    void (async () => {
      try {
        const answer = await apiFetch<RewardsSummary>(
          `/v1/wallets/${encodeURIComponent(canonical)}/rewards-summary`
        )
        if (!alive.current) return
        setSummary(answer)
        setStatus('ready')
      } catch {
        if (!alive.current) return
        // Unreachable or gated off. Keep the previous value rather than
        // flashing zeros that would read as "you lost your rewards".
        setStatus('offline')
      }
    })()
  }, [canonical])

  return { status, summary }
}
